/**
 * Pagination.tsx — the shared staff-table pagination control (UX Wave 3 §2).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 3b (Payments
 * merge, 2026-07-21).
 *
 * WHY: the Wave-3 IA redesign gives EVERY staff table/list client-side page
 * controls at 25 rows per page — one shared primitive so the controls read
 * identically everywhere (the Payments list view is the first adopter; the
 * Wave-3c pagination sweep rolls it across the remaining staff tables). The
 * component is deliberately dumb: the OWNER holds the page number and slices
 * its rows with {@link paginate}; server-side `?limit/cursor` reads (audit)
 * keep their own cursor controls until the Wave-3c sweep aligns them.
 *
 * SEMANTICS: a `<nav>` landmark (labelled per table) with Previous/Next
 * buttons and a live "Page X of Y" readout. Hidden entirely when everything
 * fits on one page — controls that can never do anything are noise.
 */

/** The Wave-3 staff-table page size — 25 rows per page, everywhere. */
export const PAGE_SIZE = 25;

/** How many pages `total` rows need (always ≥ 1 so "Page 1 of 1" never divides by zero). */
export function pageCount(total: number, pageSize: number = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * The rows visible on `page` (1-based). The page is clamped into range so a
 * filter change that shrinks the list can never strand the owner on a blank
 * page beyond the end.
 */
export function paginate<T>(items: readonly T[], page: number, pageSize: number = PAGE_SIZE): T[] {
  const clamped = Math.min(Math.max(1, page), pageCount(items.length, pageSize));
  const start = (clamped - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

const pageButtonStyle = {
  minHeight: '44px',
  padding: '8px 16px',
  borderRadius: 'var(--gps-r-pill)',
  border: '1.5px solid var(--gps-border-strong)',
  background: 'var(--gps-surface)',
  color: 'var(--gps-text)',
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
} as const;

export function Pagination({
  label,
  page,
  total,
  pageSize = PAGE_SIZE,
  onPageChange,
}: {
  /** Accessible name for the landmark, e.g. "Subscriptions pages". */
  label: string;
  /** 1-based current page (owner-held; clamped for display). */
  page: number;
  /** Total row count BEFORE slicing — the control derives the page count. */
  total: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
}) {
  const count = pageCount(total, pageSize);
  if (count <= 1) return null;
  const current = Math.min(Math.max(1, page), count);

  return (
    <nav
      aria-label={label}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--gps-s-3)' }}
    >
      <button
        type="button"
        disabled={current <= 1}
        onClick={() => onPageChange(current - 1)}
        style={{ ...pageButtonStyle, cursor: current <= 1 ? 'default' : 'pointer', opacity: current <= 1 ? 0.5 : 1 }}
      >
        Previous
      </button>
      {/* aria-live so a page change is announced without moving focus. */}
      <span aria-live="polite" style={{ fontSize: '13px', color: 'var(--gps-muted)', fontVariantNumeric: 'tabular-nums' }}>
        Page {current} of {count}
      </span>
      <button
        type="button"
        disabled={current >= count}
        onClick={() => onPageChange(current + 1)}
        style={{ ...pageButtonStyle, cursor: current >= count ? 'default' : 'pointer', opacity: current >= count ? 0.5 : 1 }}
      >
        Next
      </button>
    </nav>
  );
}
